import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import type {
  ImportActivationView,
  ImportMappingEntry,
  ImportPreview,
  ImportPreviewColumn,
  ImportResponse,
  ImportRowOutcome,
  ImportSchemaField,
  ImportSuggestionOrigin,
  ImportTarget,
  OptOutReimportMode,
  StrategySummary,
} from "@/lib/types";
import { formatDateTime } from "@/lib/format";

const TARGETS: ImportTarget[] = [
  "EMAIL",
  "PHONE",
  "FULL_NAME",
  "SOURCE",
  "TIMEZONE",
  "CUSTOM_FIELD",
];

/** Plain-language names for the targets; the enum itself is not operator vocabulary. */
const TARGET_LABELS: Record<ImportTarget, string> = {
  EMAIL: "Contact address",
  PHONE: "Phone number",
  FULL_NAME: "Name",
  SOURCE: "Source",
  TIMEZONE: "Time zone",
  CUSTOM_FIELD: "Extra field",
  IGNORE: "Not imported",
};

/** Why a column arrived pre-filled — shown so the operator can weigh a suggestion. */
const ORIGIN_LABELS: Record<ImportSuggestionOrigin, string> = {
  REMEMBERED: "from your last import",
  SCHEMA: "matches a field on this strategy",
  VALUE_SHAPE: "from the values",
  FALLBACK: "new field",
};

const FIELD_KEY_RE = /^[a-z0-9_]{1,40}$/;

/** How many per-row outcomes to print before collapsing into a count. */
const MAX_ROW_OUTCOMES = 20;

/** One column as the operator is editing it. Seeded from the server's preview. */
interface ColumnRow extends ImportPreviewColumn {
  include: boolean;
}

function toRows(preview: ImportPreview): ColumnRow[] {
  return preview.columns.map((c) => ({ ...c, include: c.target !== "IGNORE" }));
}

/**
 * Rows → mapping entries. Ordinals are renumbered per target over the included
 * columns in sheet order, so unticking the second of three address columns
 * leaves the remaining two as ordinal 0 and 1 rather than 0 and 2.
 */
export function buildEntries(rows: ColumnRow[]): ImportMappingEntry[] {
  const perTarget = new Map<ImportTarget, number>();
  return rows
    .filter((r) => r.include && r.target !== "IGNORE")
    .map((r) => {
      const ordinal = perTarget.get(r.target) ?? 0;
      perTarget.set(r.target, ordinal + 1);
      return {
        header: r.header,
        target: r.target,
        ordinal,
        fieldKey: r.target === "CUSTOM_FIELD" ? r.fieldKey : null,
      };
    });
}

/**
 * Columns the strategy does not yet declare. Declaring them is what puts them on
 * the prospect form — and what stops the CRM discarding them the first time
 * somebody edits one of these prospects.
 */
export function newSchemaFields(rows: ColumnRow[]): ImportSchemaField[] {
  return rows
    .filter((r) => r.include && r.target === "CUSTOM_FIELD" && !r.declaredInSchema && r.fieldKey)
    .map((r) => ({
      key: r.fieldKey as string,
      label: r.label,
      type: r.fieldType ?? "string",
    }));
}

/**
 * Contact-list import. Pick a strategy and a file; the server reads the sheet
 * and proposes a mapping for every column; untick what should not come in and
 * confirm. Releasing the batch to the scheduler stays a separate, explicit act —
 * importing a list and mailing a list are different decisions.
 */
export default function LeadImportDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();

  const strategiesQ = useQuery({
    queryKey: ["strategies"],
    queryFn: () => api.get<StrategySummary[]>("/api/v1/strategies"),
  });

  const [chosenStrategyId, setChosenStrategyId] = useState<string | null>(null);
  const strategyId = chosenStrategyId ?? strategiesQ.data?.[0]?.id ?? null;

  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<ColumnRow[]>([]);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [declareFields, setDeclareFields] = useState(true);
  const [rememberMapping, setRememberMapping] = useState(true);

  const previewM = useMutation({
    mutationFn: ({ chosen, sid }: { chosen: File; sid: string }) => {
      const form = new FormData();
      form.append("file", chosen);
      return api.upload<ImportPreview>(
        `/api/v1/prospect-imports/preview?strategyId=${encodeURIComponent(sid)}`,
        form,
      );
    },
    onSuccess: (data) => {
      setPreview(data);
      setRows(toRows(data));
    },
  });

  function onFileChosen(next: File | null) {
    setFile(next);
    setRows([]);
    setPreview(null);
    previewM.reset();
    if (next && strategyId) previewM.mutate({ chosen: next, sid: strategyId });
  }

  function updateRow(index: number, patch: Partial<ColumnRow>) {
    setRows((cur) => cur.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  const upload = useMutation({
    mutationFn: ({ chosen, sid, mapped }: { chosen: File; sid: string; mapped: ColumnRow[] }) => {
      const form = new FormData();
      form.append("file", chosen);
      form.append("mapping", JSON.stringify({ entries: buildEntries(mapped) }));
      if (declareFields) {
        form.append("schemaFields", JSON.stringify(newSchemaFields(mapped)));
      }
      return api.upload<ImportResponse>(
        `/api/v1/prospect-imports?strategyId=${encodeURIComponent(sid)}` +
          `&rememberMapping=${rememberMapping}`,
        form,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prospects"] });
      queryClient.invalidateQueries({ queryKey: ["strategies"] });
    },
  });

  const activate = useMutation({
    mutationFn: (batchId: string) =>
      api.post<ImportActivationView>(`/api/v1/prospect-imports/${batchId}/activate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prospects"] });
    },
  });

  const included = rows.filter((r) => r.include);
  const hasEmail = included.some((r) => r.target === "EMAIL");
  const customKeys = included
    .filter((r) => r.target === "CUSTOM_FIELD")
    .map((r) => r.fieldKey ?? "");
  const badKey = customKeys.some((k) => !FIELD_KEY_RE.test(k));
  const dupKey = new Set(customKeys).size !== customKeys.length;
  const pendingFields = newSchemaFields(rows);
  const canUpload =
    !!file && !!strategyId && included.length > 0 && hasEmail && !badKey && !dupKey &&
    !upload.isPending;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/30">
      <div className="bg-white rounded-lg shadow-lg max-w-3xl w-full max-h-[90vh] overflow-auto">
        <header className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
          <h2 className="text-lg font-semibold">Import list</h2>
          <button onClick={onClose} className="text-sm text-zinc-500 hover:text-zinc-900">×</button>
        </header>

        {upload.data ? (
          <ImportResult result={upload.data} activate={activate} onClose={onClose} />
        ) : (
          <div className="p-5 space-y-4 text-sm">
            <label className="block">
              <span className="text-xs text-[var(--fg-muted)]">Strategy</span>
              <select
                value={strategyId ?? ""}
                onChange={(e) => {
                  setChosenStrategyId(e.target.value);
                  // Suggestions are per strategy — the previous strategy's
                  // remembered mapping must not be carried into this one.
                  if (file) previewM.mutate({ chosen: file, sid: e.target.value });
                }}
                className="mt-1 w-full rounded-md border border-[var(--border)] bg-white px-2 py-1"
              >
                {strategiesQ.data?.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-xs text-[var(--fg-muted)]">File (.csv, .tsv, .xlsx, .xlsm — max 32 MiB)</span>
              <input
                type="file"
                accept=".csv,.tsv,.xlsx,.xlsm"
                onChange={(e) => onFileChosen(e.target.files?.[0] ?? null)}
                className="mt-1 block w-full text-sm"
              />
            </label>

            {previewM.isPending ? (
              <div className="text-[var(--fg-muted)]">Reading the file…</div>
            ) : null}

            {previewM.error ? (
              <div className="text-[var(--danger)]">
                {errorMessage(previewM.error, "Could not read the file")}
              </div>
            ) : null}

            {preview && rows.length > 0 ? (
              <div className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <div className="text-xs uppercase tracking-wide text-[var(--fg-muted)]">
                    Columns
                  </div>
                  <div className="text-xs text-[var(--fg-muted)]">
                    {rows.length} column{rows.length === 1 ? "" : "s"} found in the first{" "}
                    {preview.sampledRows} row{preview.sampledRows === 1 ? "" : "s"}
                    {preview.rememberedFor > 0
                      ? ` — ${preview.rememberedFor} filled in from your last import`
                      : ""}
                  </div>
                </div>

                {preview.notes.map((n, i) => (
                  <div key={i} className="text-xs text-amber-700">{n}</div>
                ))}

                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-[var(--fg-muted)]">
                    <tr>
                      <th className="py-1 pr-2 font-normal w-8">Import</th>
                      <th className="py-1 pr-2 font-normal">Column</th>
                      <th className="py-1 pr-2 font-normal">Import as</th>
                      <th className="py-1 font-normal">Field key</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => (
                      <tr key={row.index} className="border-t border-[var(--border)] align-top">
                        <td className="py-1.5 pr-2">
                          <input
                            type="checkbox"
                            aria-label={`Import ${row.header}`}
                            checked={row.include}
                            onChange={(e) =>
                              updateRow(i, {
                                include: e.target.checked,
                                // A column the preview switched off carries no
                                // target yet; turning it on has to give it one,
                                // or ticking the box would do nothing.
                                ...(e.target.checked && row.target === "IGNORE"
                                  ? {
                                      target: "CUSTOM_FIELD" as ImportTarget,
                                      fieldKey: row.fieldKey || sanitizeFieldKey(row.header),
                                      fieldType: row.fieldType ?? "string",
                                    }
                                  : {}),
                              })
                            }
                          />
                        </td>
                        <td className="py-1.5 pr-2">
                          <div className={row.include ? "font-medium" : "font-medium text-[var(--fg-muted)]"}>
                            {row.header}
                          </div>
                          {row.samples.length > 0 ? (
                            <div className="text-xs text-[var(--fg-muted)] truncate max-w-[16rem]">
                              {row.samples.join(", ")}
                            </div>
                          ) : (
                            <div className="text-xs text-[var(--fg-muted)]">no values in the sample</div>
                          )}
                        </td>
                        <td className="py-1.5 pr-2">
                          <select
                            aria-label={`Target for ${row.header}`}
                            disabled={!row.include}
                            value={row.target === "IGNORE" ? "CUSTOM_FIELD" : row.target}
                            onChange={(e) => {
                              const target = e.target.value as ImportTarget;
                              updateRow(i, {
                                target,
                                fieldKey:
                                  target === "CUSTOM_FIELD"
                                    ? row.fieldKey || sanitizeFieldKey(row.header)
                                    : row.fieldKey,
                              });
                            }}
                            className="rounded-md border border-[var(--border)] bg-white px-2 py-1 disabled:opacity-50"
                          >
                            {TARGETS.map((t) => (
                              <option key={t} value={t}>{TARGET_LABELS[t]}</option>
                            ))}
                          </select>
                          <div className="text-xs text-[var(--fg-muted)] mt-0.5">
                            {ORIGIN_LABELS[row.origin]}
                          </div>
                        </td>
                        <td className="py-1.5">
                          {row.include && row.target === "CUSTOM_FIELD" ? (
                            <input
                              aria-label={`Field key for ${row.header}`}
                              value={row.fieldKey ?? ""}
                              onChange={(e) => updateRow(i, { fieldKey: e.target.value })}
                              className={
                                "w-full rounded-md border px-2 py-1 " +
                                (FIELD_KEY_RE.test(row.fieldKey ?? "")
                                  ? "border-[var(--border)]"
                                  : "border-[var(--danger)]")
                              }
                            />
                          ) : (
                            <span className="text-[var(--fg-muted)]">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {!hasEmail && included.length > 0 ? (
                  <div className="text-xs text-[var(--danger)]">
                    Set at least one column to “Contact address” — rows without one cannot
                    become prospects.
                  </div>
                ) : null}
                {badKey ? (
                  <div className="text-xs text-[var(--danger)]">
                    Field keys must be 1–40 characters of a–z, 0–9 or underscore.
                  </div>
                ) : null}
                {dupKey ? (
                  <div className="text-xs text-[var(--danger)]">
                    Two columns map to the same field key — give each its own.
                  </div>
                ) : null}

                <div className="rounded-md border border-[var(--border)] bg-zinc-50 p-3 space-y-2 text-xs">
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={declareFields}
                      disabled={pendingFields.length === 0}
                      onChange={(e) => setDeclareFields(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-medium">
                        Add {pendingFields.length} new field
                        {pendingFields.length === 1 ? "" : "s"} to this strategy
                      </span>
                      <span className="block text-[var(--fg-muted)]">
                        Puts them on the prospect form and makes them available to phases and
                        email templates as <code>{"{{prospect.custom_fields.<key>}}"}</code>.
                        Without this, an imported column is dropped the first time the prospect
                        is edited in the CRM.
                      </span>
                    </span>
                  </label>
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={rememberMapping}
                      onChange={(e) => setRememberMapping(e.target.checked)}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="font-medium">Remember this mapping</span>
                      <span className="block text-[var(--fg-muted)]">
                        The next list with these columns arrives already mapped.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            ) : null}

            {upload.error ? (
              <div className="text-sm text-[var(--danger)]">
                {errorMessage(upload.error, "Import failed")}
              </div>
            ) : null}

            <footer className="flex items-center justify-end gap-2 pt-3 border-t border-[var(--border)]">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!canUpload}
                onClick={() => {
                  if (!file || !strategyId) return;
                  upload.mutate({ chosen: file, sid: strategyId, mapped: rows });
                }}
                className="rounded-md bg-blue-600 text-white px-3 py-1.5 text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {upload.isPending ? "Importing…" : "Import"}
              </button>
            </footer>
          </div>
        )}
      </div>
    </div>
  );
}

/** Lowercase, [a-z0-9_] only — the shape prospect_schema keys must have. */
function sanitizeFieldKey(header: string): string {
  const key = header
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
  return key || "column";
}

function errorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) return fallback;
  const body = error.body;
  if (
    typeof body === "object" && body !== null && "message" in body &&
    typeof (body as { message?: unknown }).message === "string"
  ) {
    return (body as { message: string }).message;
  }
  return `${fallback} (HTTP ${error.status})`;
}

function ImportResult({
  result,
  activate,
  onClose,
}: {
  result: ImportResponse;
  activate: UseMutationResult<ImportActivationView, Error, string>;
  onClose: () => void;
}) {
  const b = result.batch;
  return (
    <div className="p-5 space-y-4 text-sm">
      <div>
        <div className="font-medium mb-1">
          {b.status === "FAILED" ? "Import failed" : "Import finished"}
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1">
          <ResultRow k="Rows in file" v={String(b.rowsTotal)} />
          <ResultRow k="Imported" v={String(b.rowsImported)} />
          <ResultRow k="Skipped" v={String(b.rowsSkipped)} />
          <ResultRow k="Failed" v={String(b.rowsFailed)} />
          <ResultRow k="Prospects created" v={String(b.prospectsCreated)} />
        </dl>
        {b.errorSummary ? (
          <div className="mt-2 text-[var(--danger)]">{b.errorSummary}</div>
        ) : null}
      </div>

      {result.schemaFieldsAdded.length > 0 ? (
        <div className="text-xs text-[var(--fg-muted)]">
          Added to this strategy&apos;s prospect form:{" "}
          <span className="text-[var(--fg)]">{result.schemaFieldsAdded.join(", ")}</span>
        </div>
      ) : null}

      {result.warnings.length > 0 ? (
        <div>
          <div className="text-xs uppercase tracking-wide text-[var(--fg-muted)] mb-1">Warnings</div>
          <ul className="list-disc pl-5 space-y-0.5 text-amber-700">
            {result.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      ) : null}

      {result.optedOut.length > 0 ? (
        <OptedOutNotice outcomes={result.optedOut} mode={result.optOutReimport} />
      ) : null}

      <RowOutcomes label="Skipped rows" outcomes={result.skipped} />
      <RowOutcomes label="Failed rows" outcomes={result.failed} />

      {b.prospectsCreated > 0 ? (
        <div className="rounded-md border border-[var(--border)] bg-zinc-50 p-3 space-y-2">
          <div className="font-medium">Start contacting these leads</div>
          <p className="text-xs text-[var(--fg-muted)]">
            Activating the batch schedules every imported lead, staggered across the
            coming days by the strategy&apos;s daily cap on new conversations — nothing
            goes out all at once, and sends respect the strategy&apos;s send window.
          </p>
          <button
            type="button"
            disabled={activate.isPending || activate.isSuccess}
            onClick={() => activate.mutate(b.id)}
            className="rounded-md bg-emerald-600 px-3 py-1.5 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {activate.isPending ? "Activating…" : "Activate batch (paced schedule)"}
          </button>
          {activate.data ? (
            <div className="text-xs text-[var(--fg-muted)]">
              Scheduled {activate.data.activated} lead{activate.data.activated === 1 ? "" : "s"}
              {activate.data.firstDueAt
                ? ` — first due ${formatDateTime(activate.data.firstDueAt)}, last due ${formatDateTime(activate.data.lastDueAt)}`
                : ""}.
            </div>
          ) : null}
          {activate.error ? (
            <div className="text-xs text-[var(--danger)]">
              {errorMessage(activate.error, "Activation failed")}
            </div>
          ) : null}
          <p className="text-xs text-[var(--fg-muted)]">
            Prefer to start a few by hand? Leads can also be started from the list:
            tick their checkboxes and use &ldquo;Start flow&rdquo;.
          </p>
        </div>
      ) : null}

      <footer className="flex items-center justify-end gap-2 pt-3 border-t border-[var(--border)]">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm"
        >
          Done
        </button>
      </footer>
    </div>
  );
}

/**
 * SA 1.14 — rows carrying an address the strategy had already refused to mail.
 * Never quiet: under the default policy the addresses were dropped and the
 * operator should know the list still carries them; under an allowing policy
 * they are about to be mailed again, which is the one thing that must not slip
 * past unseen.
 */
function OptedOutNotice({
  outcomes,
  mode,
}: {
  outcomes: ImportRowOutcome[];
  mode: OptOutReimportMode;
}) {
  const allow = mode === "ALLOW";
  const heading = "Previously opted out";
  return (
    <div
      role="status"
      aria-label={heading}
      className={
        allow
          ? "rounded-md border border-red-300 bg-red-50 p-3 text-red-900"
          : "rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900"
      }
    >
      <div className="font-medium">{heading}</div>
      <p className="mt-0.5 mb-2 text-xs">
        {allow
          ? `${outcomes.length} row${outcomes.length === 1 ? "" : "s"} carried an address that had unsubscribed. Under this strategy's opt-out policy the address was imported again and its opt-out lifted, so it will be mailed when the batch is activated. Bounced or spam-reported addresses were still dropped.`
          : `${outcomes.length} row${outcomes.length === 1 ? "" : "s"} carried an address this strategy had already refused to mail. Those addresses were not imported; any other address on the same row was. Change the policy under Settings → Runtime if that is not what you want.`}
      </p>
      <ul className="space-y-0.5 text-xs">
        {outcomes.slice(0, MAX_ROW_OUTCOMES).map((o) => (
          <li key={o.rowNumber}>
            <span className="opacity-75">Row {o.rowNumber}:</span> {o.reason}
          </li>
        ))}
        {outcomes.length > MAX_ROW_OUTCOMES ? (
          <li className="opacity-75">…and {outcomes.length - MAX_ROW_OUTCOMES} more.</li>
        ) : null}
      </ul>
    </div>
  );
}

function RowOutcomes({ label, outcomes }: { label: string; outcomes: ImportRowOutcome[] }) {
  if (outcomes.length === 0) return null;
  const shown = outcomes.slice(0, MAX_ROW_OUTCOMES);
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-[var(--fg-muted)] mb-1">{label}</div>
      <ul className="space-y-0.5 text-xs">
        {shown.map((o) => (
          <li key={o.rowNumber}>
            <span className="text-[var(--fg-muted)]">Row {o.rowNumber}:</span> {o.reason}
          </li>
        ))}
        {outcomes.length > shown.length ? (
          <li className="text-[var(--fg-muted)]">…and {outcomes.length - shown.length} more.</li>
        ) : null}
      </ul>
    </div>
  );
}

function ResultRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-[var(--fg-muted)]">{k}</dt>
      <dd className="tabular-nums">{v}</dd>
    </div>
  );
}
